/* eslint-disable */
/**
 * Real browsers auto-expand CSS shorthand (`background`, `border`, `font`) into
 * longhand sub-properties when they parse a style attribute — read `el.style.color`
 * back and a browser gives it to you even if the source only wrote `border: 1pt
 * solid #000`. Lightweight Node DOM implementations (linkedom, confirmed in the
 * Phase 0 spike) don't do this expansion, and relying on it at all means trusting
 * browser-specific CSSOM behavior that could differ across engines too.
 *
 * Rather than depend on any engine's shorthand support, this rewrites the three
 * shorthands pasted content actually uses (Excel/Word/Docs table and text styling)
 * into explicit longhand — as a pure string transform on the raw HTML, before any
 * DOM parsing happens. Whichever engine parses the result next sees only longhand
 * declarations, so behavior is identical everywhere.
 *
 * This is a pragmatic subset, not a full CSS shorthand-syntax implementation —
 * scoped to the forms Word/Excel/Google Docs realistically emit, not the entire
 * `background`/`border`/`font` grammar.
 */

const COLOR_RE = /#[0-9a-fA-F]{3,8}\b|rgba?\([^)]+\)/;
const WIDTH_RE = /^\d+(?:\.\d+)?(?:px|pt)$/;
const BORDER_STYLE_KEYWORDS = new Set([
  "solid",
  "dashed",
  "dotted",
  "double",
  "none",
  "hidden",
  "groove",
  "ridge",
  "inset",
  "outset",
]);

function expandBackground(value: string): string[] {
  const m = value.match(COLOR_RE);
  return m ? [`background-color:${m[0]}`] : [];
}

function expandBorder(value: string): string[] {
  const out: string[] = [];
  for (const tok of value.trim().split(/\s+/)) {
    if (WIDTH_RE.test(tok)) out.push(`border-width:${tok}`);
    else if (BORDER_STYLE_KEYWORDS.has(tok.toLowerCase()))
      out.push(`border-style:${tok.toLowerCase()}`);
    else if (COLOR_RE.test(tok)) out.push(`border-color:${tok}`);
  }
  return out;
}

function expandFont(value: string): string[] {
  const out: string[] = [];
  const parts = value.trim().split(/\s+/);
  let i = 0;
  while (i < parts.length) {
    const tok = parts[i] ?? "";
    if (/^(italic|oblique)$/i.test(tok)) {
      out.push(`font-style:${tok.toLowerCase()}`);
      i++;
      continue;
    }
    if (/^(bold|bolder|lighter|[1-9]00)$/i.test(tok)) {
      out.push(`font-weight:${tok.toLowerCase()}`);
      i++;
      continue;
    }
    const sizeMatch = tok.match(/^(\d+(?:\.\d+)?(?:px|pt))(\/.+)?$/);
    if (sizeMatch) {
      out.push(`font-size:${sizeMatch[1]}`);
      i++;
      if (i < parts.length) out.push(`font-family:${parts.slice(i).join(" ")}`);
      break;
    }
    i++;
  }
  return out;
}

/** Appends longhand equivalents for any recognised shorthand declarations found. */
export function normalizeStyleAttribute(styleText: string): string {
  const declarations = styleText
    .split(";")
    .map((d) => d.trim())
    .filter(Boolean);
  const extra: string[] = [];
  for (const decl of declarations) {
    const idx = decl.indexOf(":");
    if (idx === -1) continue;
    const prop = decl.slice(0, idx).trim().toLowerCase();
    const value = decl.slice(idx + 1).trim();
    if (prop === "background") extra.push(...expandBackground(value));
    else if (prop === "border") extra.push(...expandBorder(value));
    else if (prop === "font") extra.push(...expandFont(value));
  }
  return extra.length ? `${styleText};${extra.join(";")}` : styleText;
}

/** Applies normalizeStyleAttribute to every style="..." (or style='...') attribute in raw HTML. */
export function expandShorthandInHtml(html: string): string {
  return html.replace(
    /style=(["'])((?:(?!\1).)*)\1/gi,
    (_match, quote: string, styleText: string) => {
      const decoded = styleText.replace(/&quot;/g, '"').replace(/&#39;/g, "'");
      return `style=${quote}${normalizeStyleAttribute(decoded)}${quote}`;
    }
  );
}
